import { handleActions, createAction } from 'redux-actions';
import update from 'immutability-helper';
import mapActions from '@goodwaygroup/react-map-actions';
import { get, omit, pick, sumBy } from 'lodash';

// Actions
export const actions = mapActions([
    'ADD_EXPORT_CONFIG',
    'UPDATE_EXPORT_CONFIG',
    'DESTROY_EXPORT_CONFIG',
    'REQUEST_PDF',
    'REQUEST_PDF_COMPLETE',
    'INCREASE_ACTIVE_REQUEST_COUNT',
    'DECREASE_ACTIVE_REQUEST_COUNT',
    'ADD_PENDING_ACTION',
    'REMOVE_NEXT_PENDING_ACTION'
], 'EXPORTS');


// Action Creators
export const addExportConfig = createAction(actions.ADD_EXPORT_CONFIG);
export const updateExportConfig = createAction(actions.UPDATE_EXPORT_CONFIG);
export const destroyExportConfig = createAction(actions.DESTROY_EXPORT_CONFIG);
export const requestPDF = createAction(actions.REQUEST_PDF);
export const requestPDFComplete = createAction(actions.REQUEST_PDF_COMPLETE);
export const increaseActiveRequestCount = createAction(actions.INCREASE_ACTIVE_REQUEST_COUNT);
export const decreaseActiveRequestCount = createAction(actions.DECREASE_ACTIVE_REQUEST_COUNT);
export const addPendingAction = createAction(actions.ADD_PENDING_ACTION);
export const removeNextPendingAction = createAction(actions.REMOVE_NEXT_PENDING_ACTION);

// Reducers
const initialState = {
    exportUid: 1,
    configs: new Map(), // this might break in IE 11 (edge and all other normal browsers are fine)
    inProgress: false,
    activeRequestCount: 0,
    pendingActions: []
};

function topSeries(series, limit) {
    if (series.length > limit) {
        series.sort((a, b) => sumBy(b.data, e => e.y) - sumBy(a.data, e => e.y));
        const limitedSeries = series.slice(0, limit);
        return limitedSeries;
    }
    return series;
}

function payloadProducer(payload) {
    const exportLimit = get(payload, 'config.meta.exportLimit');
    switch (payload.type) {
    case 'chart':
        if (exportLimit) {
            payload.config.series = topSeries(payload.config.series, exportLimit);
        }
        return {
            config: {
                ...payload.config,
                chart: omit(payload.config.chart, ['renderTo']) // this omit removes circular refs
            }
        };
    case 'table-with-nested-headers':
    case 'table':
        // filter out any column if 'show' is set to false
        payload.columns = payload.columns.filter(e => !('show' in e) || e.show);
        return pick(payload, [
            'title',
            'keyField',
            'data',
            'minRows',
            'exportRows',
            'columns',
            'defaultSorted',
            'meta',
            'headerDepth'
        ]);
    case 'callout-with-change':
        return pick(payload, [
            'title',
            'value',
            'deltaVisible',
            'deltaState',
            'description',
            'categories',
            'calloutWindow'
        ]);
    case 'callout-with-box-plot':
        return pick(payload, [
            'title',
            'value',
            'boxPlot',
            'seriesData',
        ]);
    case 'callout-with-treemap':
        return pick(payload, [
            'title',
            'value'
        ]);
    case 'channel-summary-callout':
        return pick(payload, [
            'channelType',
            'channelName',
            'callouts',
            'categories'
        ]);
    default:
        return {
            config: {
                ...payload.config,
                chart: omit(payload.config.chart, ['renderTo']) // this omit removes circular refs
            }
        };
    }
}

export default handleActions({
    [actions.ADD_EXPORT_CONFIG]: (state, action) => update(state, {
        configs: {
            $add: [[action.payload.exportName, {
                uid: state.exportUid,
                type: action.payload.type,
                ...(payloadProducer(action.payload))
            }]]
        },
        exportUid: { $set: state.exportUid + 1 }
    }),
    [actions.UPDATE_EXPORT_CONFIG]: (state, action) => update(state, {
        configs: {
            $add: [[action.payload.exportName, {
                uid: state.configs.get(action.payload.exportName).uid,
                type: action.payload.type,
                ...(payloadProducer(action.payload))
            }]]
        }
    }),
    [actions.DESTROY_EXPORT_CONFIG]: (state, action) => update(state, {
        configs: { $remove: [action.payload.exportName] },
    }),
    [actions.REQUEST_PDF]: state => update(state, {
        inProgress: { $set: true },
    }),
    [actions.REQUEST_PDF_COMPLETE]: state => update(state, {
        inProgress: { $set: false },
    }),
    [actions.INCREASE_ACTIVE_REQUEST_COUNT]: state => update(state, {
        activeRequestCount: { $apply: val => val + 1 },
    }),
    [actions.DECREASE_ACTIVE_REQUEST_COUNT]: state => update(state, {
        activeRequestCount: { $apply: val => (val <= 0 ? 0 : val - 1) },
    }),
    [actions.ADD_PENDING_ACTION]: (state, action) => update(state, {
        pendingActions: { $push: [action.payload] },
    }),
    [actions.REMOVE_NEXT_PENDING_ACTION]: state => update(state, {
        pendingActions: { $splice: [[0, 1]] },
    }),
}, initialState);



// WEBPACK FOOTER //
// ./src/services/exports/duck.js